/* eslint-disable @typescript-eslint/no-empty-function */
import styles from "./PropertyDetailsSectionStyles.module.scss";

import DistanceScroll from "./components/DistanceScroll";
import TagsScroll from "./components/TagsScroll";
import useWhitelabel from "hooks/useWhitelabel";
import BreadCrumbs from "@Components/BreadCrumbs";
import { getRegionBreadcrumb } from "@Pages/Search/components/SearchHeadingContent/constant";
import {
  getSortedTags,
  getTags,
  getShowPropertyInfoTags,
  milesToKilometersConverter,
  getTopAmenitySections,
  sortAmenitySections,
  buildPropertyDetailTags,
} from "./helper";
import Image from "@Components/Image";
import Skeleton from "@Components/Skeleton";
import { getLocation } from "@Pages/PropertyPage/data/helper";
import DistanceBy from "@Pages/Search/components/SearchListItem/components/DistanceBy";
import {
  getAvailableStartingPricingHelper,
  getDistanceData,
  getPricingCurrency,
  getPricingDuration,
} from "@Pages/Search/components/SearchListItem/helper";
import config from "@Config/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

import PropertyTags from "@Pages/Search/components/SearchListItem/components/PropertyTags";
import { useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getPropertyContentViewTransitionName,
  isPdpBackViewTransitionActive,
  PDP_EXPAND_TRANSITION_MS,
} from "@Utils/pdpViewTransitionNav";
import ActionButtonSection from "../ActionButtonSection";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { PAGE, CANNIBALIZATION_ROLLOUT } from "constants/configConstants";
import PropertyDetailsModal from "../PropertyDetailsModal";
import modalContainerStyles from "../PropertyDetailsModal/PropertyDetailsModal.module.scss";
import AmenitySection from "../AmenitiesSection/components/AmenitySection";
import Portal from "@Components/Portal";
import useCookies from "hooks/useCookies";
import PropertyInfoTags from "@Pages/Search/components/SearchListItem/components/PropertyInfoTag";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import BillsIncludedModal from "../AmenitiesSection/components/BillsIncludedModal";
import amenityStyles from "../AmenitiesSection/components/AmenitySectionStyles.module.scss";
import modalStyles from "../AmenitiesSection/AmenitiesSectionStyles.module.scss";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import { APP_EVENTS, APP_PAGE, CLICKED_ITEM } from "@Utils/webApputils/analyticEvents";
import { logAppEvent } from "@Utils/appAnalytics";
import PricingCompFromSlashPricing from "./components/PricingCompFromSlashPricing";
import { isEmptyObject } from "helpers/validations";
import { shouldShowSlashedPricing } from "helpers/slashedPricingHelper";
import AccreditationPill from "./components/AccreditationPill/AccreditationPill";
import useIsApp from "hooks/useIsApp";
import ReviewsBadge from "../ReviewsBadge";
import getRatingLabel from "../ReviewsBadge/helpers";
import { AppState } from "@Store/index";

const PropertyDetailsSection = (props: any) => {
  const {
    features,
    feature_tags,
    pricing,
    meta_distances,
    propertyLocation,
    name,
    destinationDistances,
    redirect_region_name,
    redirect_region_type,
    isLoading,
    isDesktop = false,
    tags,
    property_info_tags,
    regionId,
    tags_en,
    slashed_available_price,
    slashed_price_tenancy_object,
    region,
    include_accreditations,
    canonical_name,
  } = props;

  const isMiniProgram = useIsMiniProgram();
  const isApp = useIsApp();

  const inventoryId = useSelector((state: AppState) => state.propertyPageData?.data?.id);
  const [contentVtNameActive, setContentVtNameActive] = useState(true);
  const contentViewTransitionName =
    !isDesktop && inventoryId && contentVtNameActive && !isPdpBackViewTransitionActive()
      ? getPropertyContentViewTransitionName(inventoryId)
      : undefined;
  const contentTransitionStyle = contentViewTransitionName
    ? ({ viewTransitionName: contentViewTransitionName } as React.CSSProperties)
    : undefined;

  useEffect(() => {
    if (!inventoryId || isDesktop) return undefined;
    const timer = window.setTimeout(
      () => setContentVtNameActive(false),
      PDP_EXPAND_TRANSITION_MS + 100,
    );
    return () => window.clearTimeout(timer);
  }, [inventoryId, isDesktop]);

  // Gates the overview tag priority order (free / bills-included promotion).
  const isTagsPriorityEnabled = useFeatureIsOn(
    GROWTH_BOOK_FEATURE_FLAG_NAMES.PROPERTY_DETAIL_TAGS_PRIORITY,
  );
  const isMediaTabsEnabled =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.PROPERTY_PAGE_MEDIA_TABS) || config.IS_CHINA_APP;

  const reviewsRating = useSelector((state: AppState) => ({
    rating: state.propertyPageData?.data?.reviews_rating,
    reviewCount:
      state.propertyPageData?.inventoryReviewData?.length ||
      state.propertyPageData?.data?.reviews_count ||
      0,
  }));

  const overallRating = reviewsRating?.rating ? parseFloat(String(reviewsRating.rating)) : 0;
  const reviewCount = reviewsRating?.reviewCount || 0;
  const ratingLabel = getRatingLabel(overallRating);

  const handleNavigateToReviews = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("open-reviews-panel"));
    }
  };

  // Add null checks for arrays and objects
  const unsortedTags = getTags(feature_tags) || [];
  const horizontalScrollRef = useRef<any>(null);
  const tagsArray = getSortedTags(unsortedTags) || [];
  const mapTags: any = {};
  if (tags && tags_en) {
    tags?.forEach((feature: any, idx: number) => {
      mapTags[feature] = tags_en[idx]?.replace(/_/g, " ");
    });
  }
  const updatedTags = buildPropertyDetailTags({
    tagsArray,
    tags,
    features,
    mapTags,
    isPriorityEnabled: isTagsPriorityEnabled,
  });
  const isChinaAndApp = isApp || config.IS_CHINA_APP;

  const showPropertyInfoTags = getShowPropertyInfoTags(property_info_tags);

  // Add null check for propertyLocation
  const location = propertyLocation
    ? getLocation(propertyLocation, [
        "street_number",
        "route_short_name",
        "locality_long_name",
        "postal_code",
        "country_long_name",
      ]) ||
      propertyLocation?.formatted_address ||
      ""
    : "";

  // Add null check for destinationDistances
  const distanceData = destinationDistances ? getDistanceData(destinationDistances) : null;

  useEffect(() => {
    try {
      const seenPropertyScrollAnimation = !!localStorage?.getItem?.("seenPropertyScrollAnimation");
      const scrollElement = horizontalScrollRef?.current;

      if (scrollElement?.scrollWidth && !isLoading && !seenPropertyScrollAnimation) {
        const scrollToMiddle = () => {
          try {
            scrollElement.scrollTo({
              left: scrollElement.scrollWidth / 2,
              behavior: "smooth",
            });
          } catch (error) {
            console.error("Error scrolling to middle:", error);
          }
        };

        const scrollToStart = () => {
          try {
            scrollElement.scrollTo({
              left: 0,
              behavior: "smooth",
            });
          } catch (error) {
            console.error("Error scrolling to start:", error);
          }
        };

        setTimeout(scrollToMiddle, 500);
        setTimeout(scrollToStart, 1500);
        localStorage?.setItem?.("seenPropertyScrollAnimation", "true");
      }
    } catch (error) {
      console.error("Error in scroll animation:", error);
    }
  }, [isLoading]);

  const searchRegionId = useCookies("searchRegionId");
  const showCityCenterPill = regionId != searchRegionId && searchRegionId;

  const { t } = useTranslationHook();
  const { isWhitelabel } = useWhitelabel();
  const pricingDuration: any = getPricingDuration(pricing?.duration);

  const [showModal, setShowModal] = useState(false);

  // Add null checks for features array
  const featuresData = Array.isArray(features) ? [...features] : [];
  const sortedData = sortAmenitySections(featuresData?.filter((item) => item?.name));

  // Show top priority amenity sections at top level
  const topAmenitySections = getTopAmenitySections(sortedData, 2);
  const filteredData = topAmenitySections.length > 0 ? topAmenitySections : [];
  const otherAmenitiesData = sortedData?.filter(
    (amenity: any) => !topAmenitySections.includes(amenity),
  );
  const otherAmenitiesWithoutBills = otherAmenitiesData?.filter(
    (amenity: any) => (amenity.name_en || amenity.name) !== "Bills Included",
  );

  const showBillsIncluded =
    useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_BILLS_INCLUDED) || config?.IS_CHINA_APP;
  const isSlashedPricingEnabled = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SLASHED_PRICING);
  const showSlashPricing = shouldShowSlashedPricing({
    growthBookEnabled: isSlashedPricingEnabled,
    location: region?.location,
  });
  const hideAccreditations = useFeatureIsOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.SHOW_ACCREDITATIONS);
  const billsIncludedFeature = useMemo(
    () =>
      sortedData?.find((amenity: any) => (amenity.name_en || amenity.name) === "Bills Included") ||
      null,
    [JSON.stringify(sortedData)],
  );

  const billsAmenities = billsIncludedFeature?.values || [];
  const useFourColumnGrid = billsAmenities.length > 6;

  // Determine narrow panel: 650px when every section has ≤2 amenity items
  const otherSectionsMaxItems = Math.max(
    ...[...filteredData, ...otherAmenitiesWithoutBills].map(
      (section: any) => section.values?.length || 0,
    ),
    0,
  );
  const billsItemCount = (showBillsIncluded && billsAmenities.length) || 0;
  const useNarrowPanel = billsItemCount <= 2 && otherSectionsMaxItems <= 2;

  const totalCalculatedPrice = billsAmenities.reduce((sum: number, amenity: any) => {
    const price = Number(amenity.price);
    const fixedPrice = price && !Number.isNaN(price) ? parseFloat(price.toFixed(1)) : 0;
    return sum + fixedPrice;
  }, 0);

  const handleModalOpen = () => {
    try {
      gtagEventEmitter({
        action: "viewed_amenities",
        category: PAGE.INVENTORY,
        label: "success",
      });
    } catch (error) {
      console.error("Error emitting gtag event:", error);
    }
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleNavigateToMap = () => {
    try {
      if (isDesktop) {
        const e = new CustomEvent("navigate_to_section", { detail: { id: "map" } });
        window.dispatchEvent(e);
        setTimeout(() => {
          const eventMap = new CustomEvent("open_map_when_clicked");
          window.dispatchEvent(eventMap);
        }, 1000);
      } else {
        window.dispatchEvent(new CustomEvent("open_map_when_clicked"));
      }
      gtagEventEmitter({
        action: "view_map_clicked",
      });
      logAppEvent({
        eventName: APP_EVENTS.CTA_CLICKED,
        payload: {
          cta_name: CLICKED_ITEM.VIEW_MAP,
          screen_name: APP_PAGE.PROPERTY,
          section: "overview_section",
        },
      });
    } catch (error) {
      console.error("Error navigating to map:", error);
    }
  };

  const viewAllText = t("propertypage.components.propertydetailssection.viewalltext");
  const amenitiesLength = isDesktop ? 5 : 3;

  const uniqueMetaDistances = useMemo(() => {
    if (!meta_distances?.length) return [];
    const seen = new Set<string>();
    const result = meta_distances.filter((item: any) => {
      const key = `${item.place}|${item.distance}`;
      if (seen.has(key)) {
        return false; // Skip duplicate
      }
      seen.add(key);
      return true; // Keep unique item
    });
    const filtered = showCityCenterPill
      ? result
      : result.filter((dist: any) => !(dist.place || "").toLowerCase().includes("city center"));
    return filtered.slice(0, 5);
  }, [meta_distances, showCityCenterPill]);
  const getDistanceText = () => {
    const distanceText = config.IS_CHINA_APP
      ? milesToKilometersConverter(distanceData?.WALKING?.distance) || "0 km"
      : distanceData?.WALKING?.distance || "0 mi";

    if (config.IS_CHINA_APP) {
      return (
        <>
          {redirect_region_type === "locality" || redirect_region_name === " City Center"
            ? t("propertypage.components.propertydetailssection.distancefromcitycenter")
            : t("propertypage.components.propertydetailssection.distancefrom")}
          {redirect_region_name && redirect_region_name !== " City Center"
            ? ` ${t(redirect_region_name)} `
            : " "}
          {distanceText}
        </>
      );
    }

    return (
      <>
        {distanceText} {t("propertypage.components.propertydetailssection.fromdistancetext")}{" "}
        {t(redirect_region_name?.trim() || "")}{" "}
        {redirect_region_type === "locality"
          ? t("propertypage.components.propertydetailssection.citycentertext")
          : ""}
      </>
    );
  };

  const hasStartingPrice = Boolean(getAvailableStartingPricingHelper(pricing));
  const hasLowerDistanceData = Boolean(distanceData || redirect_region_name);

  const showLowerDistance = isMediaTabsEnabled && hasLowerDistanceData;

  const showLowerDistanceSkeleton =
    isMediaTabsEnabled && isDesktop && isLoading && !hasLowerDistanceData;
  const showPropertyInfoTagsSkeleton = isLoading && !isDesktop && !showPropertyInfoTags;
  const showUniversityMetaSkeleton = isLoading && !isDesktop;
  const hasMetaRow =
    uniqueMetaDistances.length > 0 ||
    showUniversityMetaSkeleton ||
    (showPropertyInfoTags && property_info_tags?.length) ||
    (!hideAccreditations && include_accreditations?.length);
  const hasLowerSectionData =
    showLowerDistance ||
    showLowerDistanceSkeleton ||
    hasMetaRow ||
    Boolean(updatedTags?.length) ||
    showUniversityMetaSkeleton ||
    showPropertyInfoTagsSkeleton;

  const renderPillSkeleton = (textWidths: number[], keyPrefix: string) => (
    <>
      {textWidths.map((textWidth, index) => (
        <div key={`${keyPrefix}-${index}`} className={styles.universityPillSkeletonWrap}>
          <div className={styles.universityPillSkeleton}>
            <Skeleton width="16px" height="16px" addBorderRadius />
            <Skeleton width={`${textWidth}px`} height="10px" addBorderRadius />
          </div>
        </div>
      ))}
    </>
  );

  const renderPropertyInfoPillsSkeleton = () =>
    renderPillSkeleton([116, 92], "property-info-pill-skeleton");

  const renderUniversityPillsSkeleton = () =>
    renderPillSkeleton([220, 160], "university-pill-skeleton");

  const renderAmenityPillsSkeleton = () => (
    <div className={styles.horizontalScroll}>
      <div className={styles.lowerLoadingPills}>
        <Skeleton width="104px" height="22px" addBorderRadius />
        <Skeleton width="80px" height="22px" addBorderRadius />
        <Skeleton width="72px" height="22px" addBorderRadius />
      </div>
    </div>
  );

  const renderLowerLoading = () => (
    <div className={styles.lowerLoading}>
      {isMediaTabsEnabled && !showLowerDistance && (
        <div className={styles.lowerLoadingDistance}>
          <Skeleton width="36px" height="36px" addBorderRadius />
          <div className={styles.lowerLoadingDistanceText}>
            <Skeleton width="72%" height="14px" mb="4px" />
            <Skeleton width="48%" height="12px" />
          </div>
        </div>
      )}
    </div>
  );

  const renderUniversityDistances = () => {
    if (showUniversityMetaSkeleton) {
      return renderUniversityPillsSkeleton();
    }
    if (uniqueMetaDistances.length > 0) {
      return uniqueMetaDistances.map((dist: any, idx: any) => (
        <DistanceScroll data={dist} key={`distanceScrol${idx}`} />
      ));
    }
    return null;
  };

  const renderPricingSection = () => {
    if (
      slashed_available_price !== null &&
      !isEmptyObject(slashed_price_tenancy_object) &&
      showSlashPricing
    ) {
      return (
        <PricingCompFromSlashPricing
          slashed_price_tenancy_object={slashed_price_tenancy_object}
          inventory={props}
          isDesktop={isDesktop}
          showBillsIncluded={false}
          showSlashPricing={showSlashPricing}
          slashed_available_price={slashed_available_price}
          t={t}
          pricing={pricing}
          pricingDuration={pricingDuration}
        />
      );
    }
    if (hasStartingPrice) {
      return (
        <>
          <div className={styles.greyText}>
            {config.IS_CHINA_APP ? "" : t("propertypage.compactinventorycard.from")}
          </div>
          <div
            data-testid="property-price"
            className={formatClassNames(styles.pricing, isDesktop && styles.desktop)}
          >
            {getPricingCurrency(pricing?.currency) ?? ""}
            {getAvailableStartingPricingHelper(pricing)}
          </div>
          <div className={styles.greyTextWithIcon}>
            {t("propertypage.components.propertydetailssection.pertext")} {t(pricingDuration || "")}
          </div>
        </>
      );
    }
    if (isLoading) {
      return (
        <>
          <Skeleton width="32px" height="12px" mb="4px" />
          <Skeleton width="55px" height="22px" mb="4px" />
          <Skeleton width="50px" height="12px" />
        </>
      );
    }
    return null;
  };

  return (
    <>
      {isDesktop && <ActionButtonSection isLoading={isLoading} isDesktop />}
      <div
        data-testid="propertypage-property-overview"
        className={formatClassNames(styles.detailContainer, isDesktop && styles.desktop)}
        id="overview"
        style={contentTransitionStyle}
      >
        {/* Upper Container - Title, Address, Price, Distance */}
        <div className={formatClassNames(styles.upperContainer, isDesktop && styles.desktop)}>
          <div data-testid="propertypage-property-details" className={styles.detailHeader}>
            <div className={styles.detailHeaderLhs}>
              {!isDesktop && region && (
                <BreadCrumbs
                  data={
                    CANNIBALIZATION_ROLLOUT.includes(region?.location?.country?.short_name || "")
                      ? [
                          ...getRegionBreadcrumb(
                            region,
                            { parent: true },
                            region?.location?.country?.short_name,
                          ),
                          { link: `/places/${canonical_name}`, name },
                        ]
                      : getRegionBreadcrumb(
                          region,
                          { parent: true },
                          region?.location?.country?.short_name,
                        )
                  }
                  dataTestId="propertypage-breadcrumb"
                  className={styles.breadcrumbs}
                  itemClassName={styles.breadcrumbItem}
                  isPropertyPage
                  hideLastItem={CANNIBALIZATION_ROLLOUT.includes(
                    region?.location?.country?.short_name || "",
                  )}
                />
              )}
              {name ? (
                <h1
                  className={formatClassNames(
                    isWhitelabel ? "wl-title-font" : "",
                    styles.propertyName,
                    isDesktop && (isChinaAndApp ? styles.chinaDesktop : styles.desktop),
                  )}
                  data-testid="propertypage-property-name"
                >
                  {name}
                </h1>
              ) : (
                isLoading && <Skeleton width="70%" height="22px" mb="8px" />
              )}
              {location ? (
                <div data-testid="propertypage-property-address" className={styles.propertyAddress}>
                  {location}
                </div>
              ) : (
                isLoading && <Skeleton width="85%" height="14px" mb="4px" />
              )}
            </div>
            <div className={styles.detailHeaderRhs}>{renderPricingSection()}</div>
          </div>
          {!isMediaTabsEnabled && (distanceData || (isLoading && !isDesktop)) && (
            <div
              data-testid="property-Distance-Info"
              className={formatClassNames(styles.detailDistance, isDesktop && styles.desktop)}
            >
              <div data-testid="property-Distance" className={styles.detailDistanceLhs}>
                <Image
                  src={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/FramelocationPin.png`}
                  height={36}
                  width={36}
                  isNotLazy={!isMiniProgram}
                  alt="Location Pin"
                />
                <div className={styles.distanceDiv}>
                  <div
                    data-testid="property-Distance-City-Center"
                    className={styles.distanceFromCenter}
                  >
                    {getDistanceText()}
                  </div>
                  {distanceData && (
                    <div className={styles.detailDistanceRhs}>
                      <DistanceBy data={distanceData} isPropertyPage />
                      {isDesktop && (
                        <span
                          data-testid="property-View-map"
                          onClick={handleNavigateToMap}
                          className={styles.viewOnMap}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleNavigateToMap();
                          }}
                        >
                          {t("propertypage.components.propertydetailssection.viewmaptext")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {!isDesktop && distanceData && (
                <span
                  data-testid="property-View-map-mobile"
                  onClick={handleNavigateToMap}
                  className={styles.viewOnMap}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleNavigateToMap();
                  }}
                >
                  {t("propertypage.components.propertydetailssection.viewmaptext")}
                </span>
              )}
            </div>
          )}
        </div>

        {!isDesktop && isMediaTabsEnabled && overallRating > 0 && (
          <ReviewsBadge
            rating={overallRating}
            ratingLabel={ratingLabel}
            reviewCount={reviewCount}
            onPress={handleNavigateToReviews}
          />
        )}
        {!isDesktop && <div className={styles.dashedSeparator} />}

        {/* Lower Container - Property Info Tags, Meta Distances, Amenities */}
        <div className={formatClassNames(styles.lowerContainer, isDesktop && styles.desktop)}>
          {showLowerDistanceSkeleton && renderLowerLoading()}
          {showLowerDistance && (
            <div
              data-testid="property-Distance-Info"
              className={formatClassNames(styles.detailDistancenew, isDesktop && styles.desktop)}
            >
              <div data-testid="property-Distance" className={styles.detailDistanceLhs}>
                <Image
                  src={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/FramelocationPin.png`}
                  height={36}
                  width={36}
                  isNotLazy={!isMiniProgram}
                  alt="Location Pin"
                />
                <div className={styles.distanceDiv}>
                  <div
                    data-testid="property-Distance-City-Center"
                    className={styles.distanceFromCenter}
                  >
                    {getDistanceText()}
                  </div>
                  {distanceData && (
                    <div className={styles.detailDistanceRhs}>
                      <DistanceBy data={distanceData} isPropertyPage />
                      {isDesktop && (
                        <span
                          data-testid="property-View-map"
                          onClick={handleNavigateToMap}
                          className={styles.viewOnMap}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleNavigateToMap();
                          }}
                        >
                          {t("propertypage.components.propertydetailssection.viewmaptext")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {!isDesktop && distanceData && (
                <span
                  data-testid="property-View-map-mobile"
                  onClick={handleNavigateToMap}
                  className={styles.viewOnMap}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleNavigateToMap();
                  }}
                >
                  {t("propertypage.components.propertydetailssection.viewmaptext")}
                </span>
              )}
            </div>
          )}
          {/* for mobile */}
          {!isDesktop && (showPropertyInfoTags || showPropertyInfoTagsSkeleton) && (
            <div className={styles.horizontalScroll}>
              {showPropertyInfoTags
                ? property_info_tags?.map((tag: any) => <PropertyInfoTags {...tag} />)
                : renderPropertyInfoPillsSkeleton()}
            </div>
          )}

          {hasMetaRow ? (
            <div className={styles.horizontalScroll}>
              {isDesktop &&
                showPropertyInfoTags &&
                property_info_tags?.map((tag: any) => <PropertyInfoTags {...tag} />)}

              {!hideAccreditations && include_accreditations?.length > 0 && (
                <AccreditationPill accreditations={include_accreditations} isDesktop={isDesktop} />
              )}

              {renderUniversityDistances()}
            </div>
          ) : (
            <></>
          )}
          {updatedTags?.length ? (
            <>
              <div
                data-testid="property-policies"
                className={styles.horizontalScroll}
                ref={horizontalScrollRef}
              >
                <PropertyTags
                  showCashbackIfOtherPropertyTagsDontHave
                  className={styles.propertyTag}
                  inventory={props}
                />
                {updatedTags.slice(0, amenitiesLength)?.map((tag: any, index: any) => (
                  <TagsScroll data={tag} key={index} mapTags={mapTags} />
                ))}
                {updatedTags.length > amenitiesLength && (
                  <button
                    className={formatClassNames(styles.viewAllButton)}
                    onClick={handleModalOpen}
                    type="button"
                  >
                    {viewAllText}
                  </button>
                )}
                {showModal && (
                  <Portal rootId="forPortal">
                    <PropertyDetailsModal
                      header={t(
                        "propertypage.components.propertydetailssection.amenitiesmodalheader",
                      )}
                      hidePropertyModal={handleModalClose}
                      isDesktop={isDesktop}
                      bodyWithNoPadding
                      sidePanel
                      removeFooter={isDesktop}
                      modalClassName={
                        isDesktop && useNarrowPanel
                          ? modalContainerStyles.sidePanelContainerNarrow
                          : ""
                      }
                    >
                      <div className={modalStyles.amenitiesContainerOuter}>
                        <div className={modalStyles.amenitiesContainerInner}>
                          {showBillsIncluded && billsAmenities.length > 0 && (
                            <div
                              className={formatClassNames(
                                amenityStyles.container,
                                amenityStyles.modalView,
                                amenityStyles.modalViewNoTopPadding,
                                filteredData.length === 0 &&
                                  otherAmenitiesWithoutBills.length === 0 &&
                                  amenityStyles.modalViewNoBottomPadding,
                              )}
                            >
                              <h3
                                data-testid="property-amenities-section-header"
                                className={formatClassNames(
                                  amenityStyles.heading,
                                  isDesktop && amenityStyles.desktop,
                                )}
                              >
                                {t("propertypage.amenitiessection.amenitysection.billsincluded")}{" "}
                                <span
                                  className={formatClassNames(
                                    amenityStyles.headingLight,
                                    amenityStyles.desktop,
                                  )}
                                >
                                  {t("propertypage.components.amenitiessection.inrent")}
                                </span>
                              </h3>
                              <BillsIncludedModal
                                dataTestId="property-amentities-modal"
                                amenities={billsAmenities}
                                totalWeeklyCost={totalCalculatedPrice}
                                isDesktop={isDesktop}
                                useFourColumnGrid={useFourColumnGrid}
                              />
                            </div>
                          )}

                          {showBillsIncluded &&
                            billsAmenities.length > 0 &&
                            (filteredData.length > 0 || otherAmenitiesWithoutBills.length > 0) && (
                              <div className={modalStyles.divider} />
                            )}

                          {filteredData.map((feature: any, idx: number) => (
                            <AmenitySection
                              dataTestId="property-amentities-modal"
                              title={feature.name}
                              titleEn={feature.name_en}
                              amenityList={feature.values}
                              withDivider={idx < filteredData.length - 1}
                              isDesktop={isDesktop}
                              amenitiesImages={[]}
                              modalView
                              showAll
                              isFirstSectionInModal={
                                !(showBillsIncluded && billsAmenities.length > 0) && idx === 0
                              }
                              isLastSectionInModal={
                                otherAmenitiesWithoutBills.length === 0 &&
                                idx === filteredData.length - 1
                              }
                              mapTags={mapTags}
                            />
                          ))}

                          {filteredData.length > 0 && otherAmenitiesWithoutBills.length > 0 && (
                            <div className={modalStyles.divider} />
                          )}

                          {otherAmenitiesWithoutBills.map((feature: any, idx: number) => (
                            <AmenitySection
                              dataTestId="property-amentities-modal"
                              title={feature.name}
                              titleEn={feature.name_en}
                              amenityList={feature.values}
                              withDivider={idx < otherAmenitiesWithoutBills.length - 1}
                              isDesktop={isDesktop}
                              amenitiesImages={[]}
                              modalView
                              showAll
                              isLastSectionInModal={idx === otherAmenitiesWithoutBills.length - 1}
                              mapTags={mapTags}
                            />
                          ))}
                        </div>
                      </div>
                    </PropertyDetailsModal>
                  </Portal>
                )}
              </div>
            </>
          ) : (
            <></>
          )}
          {isLoading && !hasLowerSectionData && !isDesktop && renderLowerLoading()}
          {isLoading && !updatedTags?.length && !isDesktop && renderAmenityPillsSkeleton()}
        </div>
      </div>

      {/* {!isLoading && meta_facts?.length > 0 ? (
        <Carousel
          className={styles.carouselContainer}
          slideList={meta_facts?.map((mf: any, idx: any) => (
            <FactsScroll data={mf} key={idx} />
          ))}
          activePos={1}
          autoSlide={meta_facts.length > 1}
          handleChangeSlide={() => {}}
        />
      ) : (
        <></>
      )} */}
    </>
  );
};

export default wrapperHOC(PropertyDetailsSection, {
  componentName: "PropertyDetailsSection-PropertyPage",
  showForChina: true,
});
